<template>
  <div data-testid="calculator-keypad" class="h-full">
    <div
      class="grid gap-[1px] bg-n-600 rounded-xl overflow-hidden border border-n-600 font-bold h-full"
      :class="{
        'grid-cols-4 max-h-[20rem] grid-rows-[1fr_3fr_1fr]': mode === CalculatorKeypadMode.FULL,
        'grid-cols-3 max-h-[16rem] grid-rows-[3fr_1fr]': mode !== CalculatorKeypadMode.FULL
      }"
    >
      <template v-if="mode === CalculatorKeypadMode.FULL">
        <div
          :class="[...keyClasses, 'text-g-500']"
          @click="$emit('action', CalculatorKeypadActions.BACKSPACE)"
        >
          <l-icon name="arrow-left" />
        </div>
        <div
          :class="[...keyClasses, 'text-g-500']"
          @click="$emit('action', CalculatorKeypadActions.DIVIDE2)"
        >
          <!-- eslint-disable-next-line -->
          {{ `/2` }}
        </div>
        <div
          :class="[...keyClasses, 'text-g-500']"
          @click="$emit('action', CalculatorKeypadActions.DIVIDE3)"
        >
          <!-- eslint-disable-next-line -->
          {{ `/3` }}
        </div>
        <div
          :class="[...keyClasses, 'text-g-500']"
          @click="$emit('action', CalculatorKeypadActions.DIVIDE)"
        >
          {{ `/` }}
        </div>
      </template>
      <div class="grid col-span-3 gap-[1px] grid-cols-3">
        <button
          v-for="key of numKeys"
          :key="key"
          :class="[...keyClasses, 'text-n-0']"
          @click="$emit('number', key)"
        >
          {{ key }}
        </button>
      </div>
      <div v-if="mode === CalculatorKeypadMode.FULL" class="grid col-span-1 gap-[1px]">
        <div
          :class="[...keyClasses, 'text-g-500']"
          @click="$emit('action', CalculatorKeypadActions.MULTIPLY)"
        >
          <l-icon name="close" />
        </div>
        <div
          :class="[...keyClasses, 'text-g-500']"
          @click="$emit('action', CalculatorKeypadActions.SUBTRACT)"
        >
          <l-icon name="minus" />
        </div>
        <div
          :class="[...keyClasses, 'text-g-500']"
          @click="$emit('action', CalculatorKeypadActions.ADD)"
        >
          <l-icon name="plus" />
        </div>
      </div>
      <div
        v-if="
          [CalculatorKeypadMode.FULL, CalculatorKeypadMode.DOUBLE_ZERO].includes(
            mode as typeof CalculatorKeypadModeType
          )
        "
        :class="[...keyClasses, 'text-n-0']"
        @click="$emit('action', CalculatorKeypadActions.DOUBLE_ZERO)"
      >
        <!-- eslint-disable-next-line -->
        {{ `00` }}
      </div>
      <div
        v-else-if="mode !== CalculatorKeypadMode.SIMPLE"
        :class="[...keyClasses, 'text-g-500']"
        @click="$emit('action', CalculatorKeypadActions.BACKSPACE)"
      >
        <l-icon name="arrow-left" />
      </div>
      <div v-else :class="[...keyClasses]"></div>

      <div :class="[...keyClasses, 'text-n-0']" @click="$emit('number', 0)">
        <!-- eslint-disable-next-line -->
        {{ '0' }}
      </div>
      <div
        v-if="mode === CalculatorKeypadMode.SIMPLE"
        :class="[...keyClasses, 'text-g-500']"
        @click="$emit('action', CalculatorKeypadActions.BACKSPACE)"
      >
        <l-icon name="arrow-left" />
      </div>
      <div
        v-else
        :class="[...keyClasses, 'text-g-500']"
        @click="$emit('action', CalculatorKeypadActions.CLEAR)"
      >
        <l-icon name="calculator" />
      </div>
      <div
        v-if="mode === CalculatorKeypadMode.FULL"
        :class="[...keyClasses, 'text-g-500']"
        @click="$emit('action', CalculatorKeypadActions.EQUALS)"
      >
        <l-icon name="equal" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core'
import { computed } from 'vue'

import { LIcon } from '@last/core-ui/paprika'

import type { CalculatorKeypadActionsType } from './types'
import { CalculatorKeypadActions, CalculatorKeypadMode, CalculatorKeypadModeType } from './types'

type Props = {
  mode: CalculatorKeypadModeType
  focused?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  mode: CalculatorKeypadMode.SIMPLE,
  focused: false
})

const emit = defineEmits<{
  number: [number]
  action: [CalculatorKeypadActionsType]
}>()

const numKeys = computed<number[]>(() => {
  let keys = Array.from({ length: 9 }, (_, i) => i + 1)
  if (props.mode !== CalculatorKeypadMode.SIMPLE) {
    keys = [7, 8, 9, 4, 5, 6, 1, 2, 3]
  }
  return keys
})

const keyClasses = [
  'select-none',
  'cursor-pointer',
  'bg-n-700',
  'h-full',
  'min-h-8',
  'max-h-16',
  'flex',
  'items-center',
  'justify-center',
  'focus-visible:outline-none',
  'transition-colors',
  'duration-150',
  'ease-in-out',
  'active:bg-n-600',
  'active:duration-0'
]

onKeyStroke(
  ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
  e => {
    if (!props.focused) return
    e.preventDefault()
    emit('number', parseInt(e.key))
  },
  { dedupe: true, passive: true }
)

onKeyStroke(
  ['Clear', 'Escape', 'Delete'],
  e => {
    if (!props.focused) return
    e.preventDefault()
    emit('action', CalculatorKeypadActions.CLEAR)
  },
  { dedupe: true, passive: true }
)

onKeyStroke(
  'Backspace',
  e => {
    if (!props.focused) return
    e.preventDefault()
    emit('action', CalculatorKeypadActions.BACKSPACE)
  },
  { dedupe: true, passive: true }
)
</script>
